import { useScrollReveal } from '@/hooks/useScrollReveal';

const milestones = [
  {
    year: 'July 2026',
    title: 'Freshhlock Goes Live',
    desc: 'The Freshhlock platform launches with a core team of logistics veterans, food technologists, and platform engineers. Our mission: digitise India\'s frozen food supply chain from warehouse to doorstep.',
    icon: 'ri-flag-line',
    highlight: false,
  },
  {
    year: 'Q4 2026',
    title: 'Kerala Pilot Programme',
    desc: 'Rolling out the first pilot in Kerala — India\'s seafood capital. Target: onboard 40 cold storage facilities and 200 reefer vehicles to validate the capacity-matching model.',
    icon: 'ri-rocket-line',
    highlight: false,
  },
  {
    year: 'Q1 — Q2 2027',
    title: 'Storage Network Expansion',
    desc: 'Opening Freshhlock Storage to warehouse owners across South India. Growing mapped capacity from 5,000 tonnes to 40,000+ tonnes with the capacity calendar, IoT monitoring dashboard, and certification tier system.',
    icon: 'ri-building-2-line',
    highlight: false,
  },
  {
    year: 'Q3 2027',
    title: 'Transport Marketplace Launch',
    desc: 'Launching Freshhlock Transport — the two-sided marketplace connecting reefer vehicle owners with produce shippers. Target: 1,200+ vehicles across 80 strategic routes in the first quarter.',
    icon: 'ri-truck-line',
    highlight: false,
  },
  {
    year: 'Q4 2027',
    title: 'Wholesale B2B Marketplace',
    desc: 'Launching Freshhlock Wholesale with 3,500+ standardised SKUs across seafood, meat, poultry, and vegetables. Introducing the Standard SKU specification system and forward-contract trading for HORECA buyers.',
    icon: 'ri-exchange-line',
    highlight: true,
  },
  {
    year: 'Q2 2028',
    title: 'Retail B2C Rollout',
    desc: 'Bringing Freshhlock Retail to 12 cities — delivering hygienic, standard-cut frozen food directly to consumers. Target: 5,000+ orders per month within the first 90 days.',
    icon: 'ri-shopping-bag-3-line',
    highlight: true,
  },
  {
    year: 'Q4 2028',
    title: 'Pan-India Network Scale',
    desc: 'Expanding to 28 cities with 340+ warehouses, 2,500+ vehicles, 8,500+ wholesale SKUs, and 12,000+ monthly retail orders. Laying the groundwork for pan-India coverage with ready-to-eat frozen food categories.',
    icon: 'ri-global-line',
    highlight: true,
  },
];

export default function TimelineSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-16 md:py-20 bg-secondary-50/30">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100/70 border border-secondary-200/50 mb-4">
            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wider">Roadmap</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary-700 mb-4">
            The Journey Ahead
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            From Kerala pilot to pan-India platform — our roadmap for connecting every link in the frozen food chain.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative">
          {/* Vertical line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-secondary-300/40 via-secondary-300/60 to-secondary-300/40 -translate-x-1/2"></div>

          <div className="space-y-0">
            {milestones.map((m, idx) => {
              const isLeft = idx % 2 === 0;
              return (
                <div key={m.title} className={`relative flex items-start ${isLeft ? 'flex-row' : 'flex-row-reverse'} mb-10 last:mb-0`}>
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 -translate-x-1/2 z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-4 border-background-50 ${
                      m.highlight ? 'bg-accent-500 text-brand-800' : 'bg-secondary-100 text-secondary-600'
                    }`}>
                      <i className={`${m.icon} text-sm`}></i>
                    </div>
                  </div>

                  {/* Content card */}
                  <div className={`w-[calc(50%-2.5rem)] ${isLeft ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className={`inline-block bg-card-50 rounded-xl border ${m.highlight ? 'border-accent-300/40' : 'border-secondary-200/30'} p-5 shadow-sm hover:shadow-md transition-all card-animate`}>
                      <span className={`text-xs font-bold ${m.highlight ? 'text-accent-600' : 'text-secondary-600'} uppercase tracking-wider`}>
                        {m.year}
                      </span>
                      <h3 className="font-heading text-sm font-bold text-foreground-900 mt-1.5 mb-2">{m.title}</h3>
                      <p className={`text-xs leading-relaxed ${isLeft ? 'text-left' : 'text-left'}`}>{m.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile list */}
        <div className="lg:hidden space-y-4">
          {milestones.map((m) => (
            <div key={m.title} className={`bg-card-50 rounded-xl border ${m.highlight ? 'border-accent-300/40' : 'border-secondary-200/30'} p-5 shadow-sm card-animate`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  m.highlight ? 'bg-accent-500 text-brand-800' : 'bg-secondary-100 text-secondary-600'
                }`}>
                  <i className={`${m.icon} text-sm`}></i>
                </div>
                <span className={`text-xs font-bold ${m.highlight ? 'text-accent-600' : 'text-secondary-600'} uppercase tracking-wider`}>
                  {m.year}
                </span>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1.5">{m.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}